import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../Config';

const PieceFormVehicule = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const reparationId = queryParams.get('reparation_id');
    
    const [formData, setFormData] = useState({
        reparation_id: reparationId || '',
        nom_piece: '',
        quantite: 1,
        prix: ''
    });
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [userRole, setUserRole] = useState('');
    const [reparation, setReparation] = useState(null);
    const [reparations, setReparations] = useState([]);

    const token = localStorage.getItem('token');

    useEffect(() => {
        const checkPermissionsAndLoadData = async () => {
            try {
                if (!token) {
                    navigate('/login');
                    return;
                }

                // Récupérer le rôle de l'utilisateur
                try {
                    const userRes = await axios.get(`${API_URL}/me`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    
                    let role = '';
                    if (userRes.data.role) {
                        role = userRes.data.role;
                    } else if (userRes.data.user && userRes.data.user.role) {
                        role = userRes.data.user.role;
                    }
                    
                    setUserRole(role);
                    
                    // Vérifier les permissions
                    const normalizedRole = role.toString().toLowerCase().trim();
                    const allowedRoles = ['mecanicien', 'magasinier', 'mécanicien'];
                    const isAllowed = allowedRoles.some(allowed => 
                        normalizedRole.includes(allowed.toLowerCase()) ||
                        allowed.toLowerCase().includes(normalizedRole)
                    );
                    
                    if (!isAllowed) {
                        alert(`Accès refusé. Seuls les mécaniciens et magasiniers peuvent ajouter des pièces.`);
                        navigate(reparationId ? `/reparations/${reparationId}` : '/reparations');
                        return;
                    }
                    
                } catch (meError) {
                    console.error('Erreur avec /me:', meError);
                    // Fallback: vérifier depuis localStorage
                    const localUser = localStorage.getItem('user');
                    if (localUser) {
                        try {
                            const parsedUser = JSON.parse(localUser);
                            if (parsedUser.role) {
                                setUserRole(parsedUser.role);
                            }
                        } catch (e) {
                            console.error('Erreur parsing user:', e);
                        }
                    }
                }
                
                // Charger les données
                if (reparationId) {
                    await fetchReparationDetails(reparationId);
                } else {
                    await fetchReparations();
                }
                
            } catch (err) {
                console.error('Erreur:', err);
                if (err.response?.status === 401) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    navigate('/login');
                } else {
                    navigate('/reparations');
                }
            }
        };

        checkPermissionsAndLoadData();
    }, [reparationId]);

    const fetchReparationDetails = async (id) => {
        try {
            const response = await axios.get(`${API_URL}/reparations/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setReparation(response.data);
        } catch (err) {
            console.error('Erreur chargement réparation:', err);
            setError('Erreur lors du chargement de la réparation');
        }
    };

    const fetchReparations = async () => {
        try {
            const response = await axios.get(`${API_URL}/reparations`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setReparations(response.data);
        } catch (err) {
            console.error('Erreur chargement réparations:', err);
            setError('Impossible de charger la liste des réparations');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'quantite' || name === 'prix' ? 
                   (value === '' ? '' : parseFloat(value)) : 
                   value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

        // Validation
        if (!formData.reparation_id) {
            setError('Veuillez sélectionner une réparation');
            setLoading(false);
            return;
        }

        if (!formData.nom_piece.trim()) {
            setError('Le nom de la pièce est requis');
            setLoading(false);
            return;
        }

        if (formData.quantite < 1) {
            setError('La quantité doit être au moins 1');
            setLoading(false);
            return;
        }

        try {
            const response = await axios.post(`${API_URL}/pieces-remplacees`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setSuccess('Pièce ajoutée avec succès');
            
            // Réinitialiser le formulaire
            setFormData(prev => ({
                ...prev,
                nom_piece: '',
                quantite: 1,
                prix: ''
            }));
            
            // Rediriger ou recharger
            if (!reparationId) {
                setTimeout(() => {
                    navigate(`/reparations/${formData.reparation_id}`);
                }, 1500);
            } else {
                await fetchReparationDetails(reparationId);
            }
            
        } catch (err) {
            console.error('Erreur API:', err);
            const errorMessage = err.response?.data?.message || 
                               'Une erreur est survenue lors de l\'ajout de la pièce';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen  flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-xl font-semibold text-gray-900 mb-2">Non authentifié</h1>
                    <p className="text-gray-600 mb-4">Veuillez vous connecter pour accéder à cette page.</p>
                    <Link
                        to="/login"
                        className="inline-block bg-gray-800 text-white px-4 py-2 rounded text-sm hover:bg-gray-700 transition"
                    >
                        Se connecter
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen  py-8">
            <div className="max-w-6xl mx-auto px-4">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="flex items-center mb-2">
                                <Link
                                    to={reparationId ? `/reparations/${reparationId}` : "/reparations"}
                                    className="text-gray-600 hover:text-gray-800 text-sm mr-4"
                                >
                                    ← Retour
                                </Link>
                                <h1 className="text-2xl font-bold text-gray-900">
                                    Ajouter une pièce remplacée
                                </h1>
                            </div>
                            <p className="text-gray-600 text-sm">
                                Enregistrez les pièces utilisées lors d'une réparation.
                            </p>
                        </div>
                        
                        {userRole && (
                            <div className="text-right">
                                <div className="text-xs text-gray-500 mb-1">Rôle</div>
                                <div className={`px-3 py-1 rounded text-sm ${
                                    ['mecanicien', 'magasinier', 'mécanicien'].includes(userRole.toLowerCase()) 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-gray-100 text-gray-800'
                                }`}>
                                    {userRole}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Formulaire principal */}
                    <div className="lg:col-span-2">
                        <div className="bg-white border border-gray-300 rounded-lg">
                            <div className="border-b border-gray-300 px-6 py-4">
                                <h2 className="text-lg font-semibold text-gray-900">Informations de la pièce</h2>
                            </div>
                            
                            <div className="px-6 py-4">
                                {/* Messages */}
                                {error && (
                                    <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                                        <div className="flex items-center">
                                            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                            <span className="font-medium">Erreur</span>
                                        </div>
                                        <p className="mt-1 ml-6 text-sm">{error}</p>
                                    </div>
                                )}
                                
                                {success && (
                                    <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                                        <div className="flex items-center">
                                            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            <span className="font-medium">Succès</span>
                                        </div>
                                        <p className="mt-1 ml-6 text-sm">{success}</p>
                                    </div>
                                )}

                                <form onSubmit={handleSubmit}>
                                    {/* Sélection réparation */}
                                    {!reparationId && (
                                        <div className="mb-6">
                                            <label className="block text-gray-700 text-sm font-medium mb-2">
                                                Réparation *
                                            </label>
                                            <select
                                                name="reparation_id"
                                                value={formData.reparation_id}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-gray-500 text-sm"
                                                disabled={reparations.length === 0 || loading}
                                            >
                                                <option value="">Sélectionner une réparation</option>
                                                {reparations.map((rep) => (
                                                    <option key={rep.id} value={rep.id}>
                                                        Réparation #{rep.id} - 
                                                        {rep.vehicule_info?.immatriculation ? ` ${rep.vehicule_info.immatriculation}` : ''}
                                                    </option>
                                                ))}
                                            </select>
                                            {reparations.length === 0 && (
                                                <p className="text-gray-500 text-xs mt-2">Aucune réparation disponible</p>
                                            )}
                                        </div>
                                    )}

                                    {/* Informations pièce */}
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-gray-700 text-sm font-medium mb-2">
                                                Nom de la pièce *
                                            </label>
                                            <input
                                                type="text"
                                                name="nom_piece"
                                                value={formData.nom_piece}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-gray-500 text-sm"
                                                placeholder="Ex: Disque de frein avant droit"
                                                disabled={loading}
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-gray-700 text-sm font-medium mb-2">
                                                    Quantité *
                                                </label>
                                                <input
                                                    type="number"
                                                    name="quantite"
                                                    value={formData.quantite}
                                                    onChange={handleChange}
                                                    required
                                                    min="1"
                                                    step="1"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-gray-500 text-sm"
                                                    disabled={loading}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-gray-700 text-sm font-medium mb-2">
                                                    Prix unitaire (MAD)
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type="number"
                                                        name="prix"
                                                        value={formData.prix}
                                                        onChange={handleChange}
                                                        min="0"
                                                        placeholder="0.00"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-gray-500 text-sm"
                                                        disabled={loading}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Calcul total */}
                                    {formData.quantite > 0 && formData.prix && (
                                        <div className="mt-4 p-3  border border-gray-200 rounded">
                                            <div className="flex justify-between items-center">
                                                <div className="text-sm text-gray-600">
                                                    {formData.quantite} × {parseFloat(formData.prix).toFixed(2)} MAD
                                                </div>
                                                <div className="text-lg font-bold text-gray-900">
                                                    {(formData.quantite * parseFloat(formData.prix)).toFixed(2)} MAD
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Boutons */}
                                    <div className="mt-6 pt-4 border-t border-gray-300 flex justify-end space-x-3">
                                        <Link
                                            to={reparationId ? `/reparations/${reparationId}` : "/reparations"}
                                            className="px-4 py-2 text-gray-700 text-sm border border-gray-300 rounded hover: transition"
                                        >
                                            Annuler
                                        </Link>
                                        
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="px-4 py-2 bg-gray-800 text-white text-sm rounded hover:bg-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                        >
                                            {loading ? (
                                                <>
                                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Ajout en cours...
                                                </>
                                            ) : (
                                                'Ajouter la pièce'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>

                    {/* Colonne info */}
                    <div className="space-y-6">
                        {/* Info réparation */}
                        {reparation ? (
                            <div className="bg-white border border-gray-300 rounded-lg">
                                <div className="border-b border-gray-300 px-6 py-4">
                                    <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                                        Réparation #{reparation.id}
                                    </h2>
                                </div>
                                <div className="px-6 py-4 space-y-3">
                                    <div>
                                        <div className="text-xs text-gray-500 mb-1">Description</div>
                                        <div className="text-sm text-gray-900  p-2 rounded">
                                            {reparation.description_finale?.substring(0, 100) || 'Aucune description'}
                                            {reparation.description_finale?.length > 100 && '...'}
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <div className="text-xs text-gray-500 mb-1">Coût total</div>
                                            <div className="text-sm font-medium text-gray-900">
                                                {parseFloat(reparation.montant || 0).toFixed(2)} MAD
                                            </div>
                                        </div>
                                        
                                        <div>
                                            <div className="text-xs text-gray-500 mb-1">Mécanicien</div>
                                            <div className="text-sm text-gray-900">
                                                {reparation.mecanicien?.nom || 'Non spécifié'}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <div className="text-xs text-gray-500 mb-1">Date</div>
                                        <div className="text-sm text-gray-900">
                                            {new Date(reparation.created_at).toLocaleDateString('fr-FR')}
                                        </div>
                                    </div>
                                    
                                    {reparation.pieces && reparation.pieces.length > 0 && (
                                        <div className="pt-3 border-t border-gray-200">
                                            <div className="text-xs text-gray-500 mb-1">Pièces déjà ajoutées</div>
                                            <div className="text-sm font-medium text-gray-900">
                                                {reparation.pieces.length} pièce(s)
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : reparationId ? (
                            <div className="bg-white border border-gray-300 rounded-lg p-6">
                                <div className="text-center text-gray-500">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-300 mx-auto mb-3"></div>
                                    <p className="text-sm">Chargement de la réparation...</p>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white border border-gray-300 rounded-lg">
                                <div className="border-b border-gray-300 px-6 py-4">
                                    <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                                        Aperçu
                                    </h2>
                                </div>
                                <div className="px-6 py-4">
                                    <p className="text-sm text-gray-600 mb-3">
                                        Sélectionnez une réparation dans la liste pour voir ses détails ici.
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        Vous pourrez ensuite ajouter les pièces utilisées lors de cette réparation.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Info véhicule */}
                        {reparation && (reparation.vehicule || reparation.demande?.vehicule) && (
                            <div className="bg-white border border-gray-300 rounded-lg">
                                <div className="border-b border-gray-300 px-6 py-4">
                                    <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                                        Véhicule
                                    </h2>
                                </div>
                                <div className="px-6 py-4 space-y-3">
                                    <div>
                                        <div className="text-xs text-gray-500 mb-1">Immatriculation</div>
                                        <div className="text-sm font-medium text-gray-900">
                                            {reparation.vehicule?.immatriculation || reparation.demande?.vehicule?.immatriculation}
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <div className="text-xs text-gray-500 mb-1">Marque & Modèle</div>
                                        <div className="text-sm text-gray-900">
                                            {reparation.vehicule?.marque || reparation.demande?.vehicule?.marque}{' '}
                                            {reparation.vehicule?.modele || reparation.demande?.vehicule?.modele}
                                        </div>
                                    </div>
                                    
                                    {reparation.vehicule?.id && (
                                        <Link
                                            to={`/vehicules/${reparation.vehicule.id}`}
                                            className="inline-block text-xs text-gray-600 hover:text-gray-800"
                                        >
                                            Voir détails →
                                        </Link>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PieceFormVehicule;